import { useState } from 'react'
import { useZero } from '@rocicorp/zero/react'
import { dateInputToUtcMs, todayDateInput } from '../lib/fine-import/parse.ts'
import type { FeeType } from '../lib/fine-import/types.ts'
import { mutators } from '../zero/mutators.ts'

export function AddFeeForm({ userId }: { userId: string }) {
  const z = useZero()
  const [amount, setAmount] = useState('2.00')
  const [type, setType] = useState<FeeType>('Fine')
  const [description, setDescription] = useState('')
  const [feeDate, setFeeDate] = useState(todayDateInput)
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    const amountNumber = Number(amount)
    const amountCents = Math.round(amountNumber * 100)
    const feeDateMs = dateInputToUtcMs(feeDate)
    if (!Number.isFinite(amountNumber) || amountCents <= 0) {
      setMessage('Enter an amount greater than zero')
      return
    }
    if (feeDateMs == null) {
      setMessage('Choose a valid fee date')
      return
    }

    setBusy(true)
    setMessage('')
    try {
      const mutation = z.mutate(
        mutators.fees.add({
          id: crypto.randomUUID(),
          userId,
          amountCents,
          type,
          description: description.trim() || null,
          feeDate: feeDateMs,
        }),
      )
      const clientResult = await mutation.client
      if (clientResult.type === 'error') {
        setMessage(clientResult.error.message)
        return
      }

      setAmount('2.00')
      setType('Fine')
      setDescription('')
      setMessage('Fee added · syncing…')
      void mutation.server.then((serverResult) => {
        setMessage(serverResult.type === 'success' ? 'Fee added' : serverResult.error.message)
      })
    } catch {
      setMessage('Failed to add fee')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="card form-stack" onSubmit={submit}>
      <h2>Add fee</h2>
      <div className="form-grid">
        <label>
          Amount (NZD)
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            required
          />
        </label>
        <label>
          Type
          <select value={type} onChange={(event) => setType(event.target.value as FeeType)}>
            <option value="Fine">Fine</option>
            <option value="Registration">Registration</option>
            <option value="Expense">Expense</option>
          </select>
        </label>
        <label>
          Fee date
          <input
            type="date"
            value={feeDate}
            onChange={(event) => setFeeDate(event.target.value)}
            required
          />
        </label>
      </div>
      <label>
        Description (optional)
        <input
          maxLength={500}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>
      <button className="btn" type="submit" disabled={busy}>
        {busy ? 'Adding…' : 'Add fee'}
      </button>
      {message ? <p className="form-message">{message}</p> : null}
    </form>
  )
}
